import React from 'react';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
interface PathwayDocumentProps {
  pathwayName: string;
  company: string;
  cohort: Cohort | null;
  audience: AudienceLevel | 'any';
  /** Module ids in the order the prospect should take them. */
  items: string[];
  /** Optional field labels the rep ticked in the send dialog. */
  fields?: string[];
  /** Render only the cover page — used for the thumbnail in the send dialog. */
  coverOnly?: boolean;
}
const MODULES_PER_PAGE = 3;
export function pageCount(itemCount: number): number {
  return 1 + Math.max(1, Math.ceil(itemCount / MODULES_PER_PAGE));
}
function chunk(items: string[]): string[][] {
  const pages: string[][] = [];
  for (let i = 0; i < items.length; i += MODULES_PER_PAGE) {
    pages.push(items.slice(i, i + MODULES_PER_PAGE));
  }
  return pages;
}
function Page({
  children,
  footerLabel,
  pageNumber,
  totalPages
}: {
  children: React.ReactNode;
  footerLabel: string;
  pageNumber: number;
  totalPages: number;
}) {
  return <div className="pathway-page flex h-[1056px] w-[816px] flex-col bg-white shadow-lg" data-id="element-1728" data-mp="2yim4n0n03xa5">
      <div className="h-2 w-full flex-shrink-0 bg-[#DB3725]" data-id="element-1729" data-mp="33gf7bjp3i95o" />
      <div className="flex flex-1 flex-col px-16 py-12" data-id="element-1730" data-mp="38eyx5v6t853b">{children}</div>
      <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-200 px-16 py-5" data-id="element-1731" data-mp="2ar8b4rd45vnl">
        <span className="text-[10px] text-gray-500" data-id="element-1732" data-mp="2fpi7346nx0a9">{footerLabel}</span>
        <span className="text-[10px] text-gray-500" data-id="element-1733" data-mp="2fnoy3i8ku3z3">
          Page {pageNumber} of {totalPages}
        </span>
      </div>
    </div>;
}
function Wordmark() {
  return <div className="flex items-baseline gap-2" data-id="element-1734" data-mp="2phfj2d5hvwa1">
      <span className="text-lg font-bold text-[#DB3725]" data-id="element-1735" data-mp="1s0qisnw7122r">
        University of Phoenix
      </span>
      <span className="text-sm font-semibold text-gray-700" data-id="element-1736" data-mp="1wza5iqim2hw6">
        Professional Development
      </span>
    </div>;
}
export function PathwayDocument({
  pathwayName,
  company,
  cohort,
  audience,
  items,
  fields = [],
  coverOnly = false
}: PathwayDocumentProps) {
  const showCategory = fields.includes('Category');
  const showTopics = fields.includes('Topics');
  const showModuleId = fields.includes('Skill Module ID');
  const contentPages = chunk(items);
  const totalPages = contentPages.length + 1;
  const recipient = company.trim() || 'your team';
  const today = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const footerLabel = `${pathwayName || 'Recommended pathway'} · Prepared for ${recipient}`;
  return <div className="flex flex-col items-center gap-8" data-id="element-1737" data-mp="1azcmjp61kjwm">
      <Page footerLabel="University of Phoenix · Professional Development" pageNumber={1} totalPages={totalPages} data-id="element-1738" data-mp="1axruc6779d1x">
        <Wordmark data-id="element-1739" data-mp="2meriqqqff7qo" />

        <div className="mt-20" data-id="element-1740" data-mp="2md6q56no9yvg">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#30718D]" data-id="element-1741" data-mp="2mdzw45et4kcj">
            Recommended learning pathway
          </div>
          <h1 className="mt-3 text-[40px] font-bold leading-tight text-gray-900" data-id="element-1742" data-mp="d63cb3rl930b">
            {pathwayName || 'Recommended Learning Pathway'}
          </h1>
          <p className="mt-4 text-lg text-gray-700" data-id="element-1743" data-mp="3vd9koa7px4ne">
            Prepared for {recipient}
            {cohort ? ` · ${cohort.name}` : ''}
          </p>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1744" data-mp="ru7wyda7bolq">{today}</p>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-6 border-y border-gray-200 py-6" data-id="element-1745" data-mp="vvteibxokr29">
          <div data-id="element-1746" data-mp="i4o90t7mt4k0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1747" data-mp="i56f0hk3ix54">
              Built for
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1748" data-mp="n26m8vsom5xt">
              {audience === 'any' ? 'Mixed' : LEVEL_LABELS[audience]}
            </div>
          </div>
          <div data-id="element-1749" data-mp="1fkaisbnivybw">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1750" data-mp="1fku2nxwqgwsk">
              Modules
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1751" data-mp="3n1iwyufi48bz">
              {items.length}
            </div>
          </div>
          <div data-id="element-1752" data-mp="3e2no1al2ok4z">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1753" data-mp="3e25mpzjjzfq7">
              Total time
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1754" data-mp="395ovuxkyyn9f">
              {formatHours(totalHours(items))}
            </div>
          </div>
        </div>

        <p className="mt-8 max-w-[34rem] text-sm leading-relaxed text-gray-700" data-id="element-1755" data-mp="5mvncz71som4">
          This pathway was built from our conversation about{' '}
          {recipient === 'your team' ? 'your team' : recipient} and is listed in
          the order we recommend taking it. It is designed for{' '}
          <span className="font-semibold" data-id="element-1756" data-mp="tcyb533xj8ex">
            {audience === 'any' ? 'a mixed-experience group' : LEVEL_LABELS[audience].toLowerCase()}
          </span>
          .
        </p>

        <div className="mt-10" data-id="element-1757" data-mp="vy612ab3l5rz">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1758" data-mp="vz8uopzt1mbl">
            At a glance
          </div>
          <ol className="mt-3 space-y-2" data-id="element-1759" data-mp="15vk85jb4si7m">
            {items.map((moduleId, index) => {
            const module = moduleById(moduleId);
            if (!module) return null;
            return <li key={moduleId} className="flex items-baseline gap-3" data-id="element-1760" data-mp="1ewa36kski4ka" data-mpi={index}>
                  <span className="w-5 flex-shrink-0 text-xs font-bold text-[#30718D]" data-id="element-1761" data-mp="1eu5wxw774tgu">
                    {index + 1}
                  </span>
                  <span className="flex-1 text-sm font-semibold text-gray-900" data-id="element-1762" data-mp="1omuwbsph2gvt">
                    {module.name}
                  </span>
                </li>;
          })}
          </ol>
        </div>

        <div className="mt-auto pt-10" data-id="element-1763" data-mp="1xnszk8s36kdc">
          <p className="text-[10px] leading-relaxed text-gray-500" data-id="element-1764" data-mp="1xknf56uek6ny">
            Prepared by University of Phoenix Professional Development. Module
            content and availability are subject to change. Contact your account
            team for enrollment and pricing.
          </p>
        </div>
      </Page>

      {!coverOnly && contentPages.map((page, pageIndex) => <Page key={`modules-${pageIndex}`} footerLabel={footerLabel} pageNumber={pageIndex + 2} totalPages={totalPages} data-id="element-1765" data-mp="cb7udwcjjymv" data-mpi={pageIndex}>
            <div className="flex items-baseline justify-between border-b border-gray-200 pb-4" data-id="element-1766" data-mp="m5hllapqyqw0">
              <div data-id="element-1767" data-mp="h7pxysw4xqxm">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#30718D]" data-id="element-1768" data-mp="hakfabjuqvmt">
                  The pathway
                  {pageIndex > 0 ? ' (continued)' : ''}
                </div>
                <h2 className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1769" data-mp="q736njcfqbha">
                  Modules {pageIndex * MODULES_PER_PAGE + 1}–
                  {pageIndex * MODULES_PER_PAGE + page.length} of {items.length}
                </h2>
              </div>
              <span className="text-xs text-gray-500" data-id="element-1770" data-mp="17j9tv5eznaym">
                Take these in the order shown.
              </span>
            </div>

            <div className="mt-2" data-id="element-1771" data-mp="1cj5viy07qqb0">
              {page.map((moduleId, indexOnPage) => {
          const module = moduleById(moduleId);
          if (!module) return null;
          return <article key={moduleId} className="border-b border-gray-100 py-6 last:border-b-0" data-id="element-1772" data-mp="orqo4qtkxisw" data-mpi={indexOnPage}>
                    <div className="flex items-baseline gap-3" data-id="element-1773" data-mp="tseow47m1e0b">
                      <span className="text-lg font-bold text-[#DB3725]" data-id="element-1774" data-mp="fru6va9irm7v">
                        {pageIndex * MODULES_PER_PAGE + indexOnPage + 1}
                      </span>
                      <div data-id="element-1775" data-mp="2a53ib7lmlhsr">
                        <h3 className="text-[17px] font-bold leading-snug text-gray-900" data-id="element-1776" data-mp="2a4tt3m8eneo4">
                          {module.name}
                        </h3>
                        {(showCategory || showModuleId) && <p className="mt-0.5 text-xs text-gray-500" data-id="element-1777" data-mp="11wsbcerkedfv">
                            {[showCategory ? module.category : null, showModuleId ? module.courseId : null].filter(Boolean).join(' · ')}
                          </p>}
                      </div>
                    </div>

                    <dl className="mt-4 space-y-3 pl-7" data-id="element-1778" data-mp="e7hirkkdbpct">
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1779" data-mp="e9tv9i7cx7ih">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1780" data-mp="9967d5efd6sh">
                          Description
                        </dt>
                        <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1781" data-mp="23mxbxqhwd0h2">
                          {module.description}
                        </dd>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1782" data-mp="2754p7brs938d">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1783" data-mp="2b66ottpm3hav">
                          Relevance
                        </dt>
                        <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1784" data-mp="2fxopegnq3fvb">
                          {module.relevance}
                        </dd>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1785" data-mp="1o721r1evveco">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1786" data-mp="1s6kdb6kted2u">
                          Expected outcomes
                        </dt>
                        <dd data-id="element-1787" data-mp="2258j96ziz1q6">
                          <ul className="space-y-1" data-id="element-1788" data-mp="226jkp05hzxe4">
                            {module.outcomes.map(outcome => <li key={outcome} className="flex gap-2 text-[13px] leading-relaxed text-gray-800" data-id="element-1789" data-mp="38l2ddp9g113a">
                                <span aria-hidden="true" className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-[#30718D]" data-id="element-1790" data-mp="288yoymt2hey4" />
                                {outcome}
                              </li>)}
                          </ul>
                        </dd>
                      </div>
                      {showTopics && <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1791" data-mp="1u8ny3jrrc0cy">
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1792" data-mp="1ybasnsftbzdu">
                            Topics
                          </dt>
                          <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1793" data-mp="2381aak79n31d">
                            {module.topics.join(' · ')}
                          </dd>
                        </div>}
                    </dl>
                  </article>;
        })}
            </div>
          </Page>)}
    </div>;
}